import { AfterViewInit, Component, computed, OnDestroy, Renderer2, ViewChild } from '@angular/core';
import { animate, query, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, Subscription } from 'rxjs';
import { AppTopbar } from './app.topbar';
import { AppSidebar } from './app.sidebar';
import { LayoutService } from '@core/diamond/service/layout.service';
import { AppConfigurator } from './app.configurator';
import { AppBreadcrumb } from '@core/diamond/components/app.breadcrumb';
import { AppFooter } from '@core/diamond/components/app.footer';
import { AppSearch } from '@core/diamond/components/app.search';
import { AppRightMenu } from '@core/diamond/components/app.rightmenu';
import { LangService, TimezoneService } from '@shared/services';
import { Store } from '@ngrx/store';
import { SessionState, selectSelectedCompany, selectUiPreferences } from '@app/core/state/session';
import { AppConfig } from '@app/core/domain/appconfig';
import { ToastComponent } from '@shared/components/toast/toast.component';
import { EnvTagComponent } from '@app/core/components/env-tag/env-tag.component';
import { ConfirmComponent } from '@shared/components/confirm/confirm.component';

@Component({
  selector: 'app-layout',
  standalone: true,
  imports: [
    CommonModule,
    AppTopbar,
    AppSidebar,
    RouterModule,
    AppConfigurator,
    AppBreadcrumb,
    AppFooter,
    AppSearch,
    AppRightMenu,
    ToastComponent,
    EnvTagComponent,
    ConfirmComponent,
  ],
  template: `<div class="layout-wrapper" [ngClass]="containerClass()">
    <div app-sidebar></div>
    <div class="layout-content-wrapper">
      <div class="layout-content-wrapper-inside">
        <app-env-tag></app-env-tag>
        <div app-topbar></div>
        <div class="layout-content">
          <div app-breadcrumb></div>
          <div class="route-shell" [@routeAnimations]="router.url">
            <router-outlet></router-outlet>
          </div>
        </div>
        <div app-footer></div>
      </div>
    </div>
    <app-configurator />
    <app-toast></app-toast>
    <app-confirm></app-confirm>
    <div app-search></div>
    <div app-rightmenu></div>
    <div class="layout-mask animate-fadein"></div>
  </div> `,
  styles: [
    `
      .route-shell {
        position: relative;
        overflow: hidden;
        display: block;
      }

      .route-shell > * {
        display: block;
      }
    `,
  ],
  animations: [
    trigger('routeAnimations', [
      transition('* <=> *', [
        query(
          ':enter',
          [
            style({ opacity: 0, transform: 'translateY(12px)' }),
            animate(
              '800ms 60ms cubic-bezier(0.22, 1, 0.36, 1)',
              style({ opacity: 1, transform: 'translateY(0)' }),
            ),
          ],
          { optional: true },
        ),
      ]),
    ]),
  ],
})
export class AppLayout implements AfterViewInit, OnDestroy {
  overlayMenuOpenSubscription: Subscription;

  menuOutsideClickListener: any;

  menuScrollListener: any;

  @ViewChild(AppSidebar) appSidebar!: AppSidebar;

  @ViewChild(AppTopbar) appTopBar!: AppTopbar;

  @ViewChild(AppConfigurator) appConfigurator?: AppConfigurator;

  subscription: Subscription = new Subscription();

  private viewInitialized = false;

  private pendingPreferences: AppConfig | null = null;

  constructor(
    public layoutService: LayoutService,
    public renderer: Renderer2,
    public router: Router,
    private timezoneService: TimezoneService,
    private langService: LangService,
    private store: Store<SessionState>,
  ) {
    this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
      if (!this.menuOutsideClickListener) {
        this.menuOutsideClickListener = this.renderer.listen('document', 'click', (event) => {
          if (this.isOutsideClicked(event)) {
            this.hideMenu();
          }
        });
      }
      if (
        (this.layoutService.isHorizontal() ||
          this.layoutService.isSlim() ||
          this.layoutService.isCompact()) &&
        !this.menuScrollListener
      ) {
        this.menuScrollListener = this.renderer.listen(
          this.appSidebar.menuContainer.nativeElement,
          'scroll',
          (event) => {
            if (this.layoutService.isDesktop()) {
              this.hideMenu();
            }
          },
        );
      }
      if (this.layoutService.layoutState().staticMenuMobileActive) {
        this.blockBodyScroll();
      }
    });

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.hideMenu();
    });

    this.subscription.add(
      this.store.select(selectUiPreferences).subscribe((preferences) => {
        if (preferences) {
          if (this.viewInitialized) {
            this.applyPreferences(preferences);
          } else {
            this.pendingPreferences = preferences;
          }
          this.langService.setLang(preferences.lang);
        }
      }),
    );

    this.subscription.add(
      this.store.select(selectSelectedCompany).subscribe((company) => {
        this.timezoneService.setCurrentTimezoneFromCompany(company);
      }),
    );
  }

  ngAfterViewInit(): void {
    this.viewInitialized = true;
    if (this.pendingPreferences) {
      this.applyPreferences(this.pendingPreferences);
      this.pendingPreferences = null;
    }
  }

  private applyPreferences(preferences: AppConfig): void {
    this.layoutService.layoutConfig.update((state) => ({
      ...state,
      preset: preferences.preset ?? state.preset,
      primary: preferences.primaryColor ?? state.primary,
      surface: preferences.surfaceColor ?? state.surface,
      darkTheme: preferences.darkTheme ?? state.darkTheme,
      cardStyle: preferences.cardStyle ?? state.cardStyle,
      menuTheme: preferences.menuTheme ?? state.menuTheme,
      menuMode: preferences.menuMode ?? state.menuMode,
    }));

    this.appConfigurator?.onPresetChange(this.layoutService.layoutConfig().preset);
    this.appConfigurator?.updateMenuThemeOptions();
  }

  isOutsideClicked(event: any) {
    const sidebarEl = document.querySelector('.layout-sidebar');
    const topbarButtonEl = document.querySelector('.menu-button');

    return !(
      sidebarEl?.isSameNode(event.target) ||
      sidebarEl?.contains(event.target) ||
      topbarButtonEl?.isSameNode(event.target) ||
      topbarButtonEl?.contains(event.target)
    );
  }

  hideMenu() {
    this.layoutService.layoutState.update((prev) => ({
      ...prev,
      overlayMenuActive: false,
      staticMenuMobileActive: false,
      menuHoverActive: false,
    }));
    this.layoutService.reset();
    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }

    this.unblockBodyScroll();
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp('(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'),
        ' ',
      );
    }
  }

  containerClass = computed(() => {
    const layoutConfig = this.layoutService.layoutConfig();
    const layoutState = this.layoutService.layoutState();

    return {
      'layout-overlay': layoutConfig.menuMode === 'overlay',
      'layout-static': layoutConfig.menuMode === 'static',
      'layout-slim': layoutConfig.menuMode === 'slim',
      'layout-horizontal': layoutConfig.menuMode === 'horizontal',
      'layout-compact': layoutConfig.menuMode === 'compact',
      'layout-reveal': layoutConfig.menuMode === 'reveal',
      'layout-drawer': layoutConfig.menuMode === 'drawer',
      'layout-overlay-active': layoutState.overlayMenuActive || layoutState.staticMenuMobileActive,
      'layout-mobile-active': layoutState.staticMenuMobileActive,
      'layout-static-inactive':
        layoutState.staticMenuDesktopInactive && layoutConfig.menuMode === 'static',
      'layout-sidebar-active': layoutState.sidebarActive,
      'layout-sidebar-anchored': layoutState.anchored,
      [`layout-card-${layoutConfig.cardStyle}`]: true,
      [`layout-sidebar-${layoutConfig.menuTheme}`]: true,
    };
  });

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
    this.subscription.unsubscribe();
  }
}
